import { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search, X, PawPrint, MapPin, Sparkles, CornerDownLeft, Scale } from "lucide-react";
import { PALS_BY_INDEX } from "@/lib/breeding";
import { MAP_POIS } from "@/data/mapPois";
import { PASSIVES } from "@/data/passives";
import { PalAvatar } from "@/components/PalAvatar";
import { usePalDetail } from "@/components/PalDetailModal";
import { usePalCompare } from "@/components/PalCompareModal";

// Lightweight POI category list (mirrors MapPage POI_CATS) to avoid importing leaflet here.
const POI_CATS = [
  { key: "fasttravel", label: "Voyage rapide", color: "#00CCFF" },
  { key: "tower", label: "Tour du Syndicat", color: "#D946EF" },
  { key: "alpha", label: "Pal Alpha", color: "#EF4444" },
  { key: "dungeon", label: "Donjon", color: "#8B5CF6" },
  { key: "skillfruit", label: "Fruit de compétence", color: "#10B981" },
  { key: "sealed", label: "Royaume scellé", color: "#F59E0B" },
];

function norm(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function GlobalSearch({ open, onClose }) {
  const navigate = useNavigate();
  const openPal = usePalDetail();
  const openCompare = usePalCompare();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const results = useMemo(() => {
    const term = norm(q).trim();
    if (!term) return [];
    const pals = [];
    const maps = [];
    const passives = [];

    // Pals
    PALS_BY_INDEX.forEach((p) => {
      if (!p) return;
      if (norm(p.fr).includes(term) || norm(p.name).includes(term)) {
        pals.push({ type: "pal", key: `pal-${p.i}`, label: p.fr, sub: p.fr !== p.name ? p.name : "Pal", pal: p, idx: p.i });
      }
    });

    // Alpha bosses (map spots) by pal name
    MAP_POIS.forEach((m) => {
      if (m.c !== "alpha" || m.idx == null) return;
      const pp = PALS_BY_INDEX[m.idx];
      if (pp && (norm(pp.fr).includes(term) || norm(pp.name).includes(term))) {
        maps.push({ type: "map", key: `alpha-${m.idx}`, label: `${pp.fr} (Alpha)`, sub: `Pal Alpha${m.lvl ? ` · Nv. ${m.lvl}` : ""}`, color: "#EF4444", focus: `alpha:${m.idx}` });
      }
    });

    // POI categories
    POI_CATS.forEach((c) => {
      if (norm(c.label).includes(term)) {
        maps.push({ type: "map", key: `poi-${c.key}`, label: c.label, sub: "Calque de POI", color: c.color, focus: `poi:${c.key}` });
      }
    });

    // Passive skills
    PASSIVES.forEach((r) => {
      if (norm(r.name).includes(term) || norm(r.desc).includes(term)) {
        passives.push({ type: "passive", key: `passive-${r.id}`, label: r.name, sub: `Passif${r.rank ? ` · rang ${r.rank > 0 ? "+" : ""}${r.rank}` : ""}`, color: "#EAB308" });
      }
    });

    // Per-group cap so one group never drowns out the others.
    return [...pals.slice(0, 8), ...maps.slice(0, 8), ...passives.slice(0, 8)];
  }, [q]);

  useEffect(() => { setSel(0); }, [q]);

  const choose = (r) => {
    if (!r) return;
    onClose();
    if (r.type === "pal") {
      openPal(r.idx);
    } else if (r.type === "map") {
      navigate(`/map?focus=${encodeURIComponent(r.focus)}`);
    } else if (r.type === "passive") {
      navigate(`/passives`);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(results[sel]); }
  };

  if (!open) return null;

  const GROUP_ICON = { pal: PawPrint, map: MapPin, passive: Sparkles };

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-start justify-center p-4 pt-[12vh] bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="global-search-overlay"
    >
      <div
        className="glass w-full max-w-xl rounded-xl border border-border shadow-2xl overflow-hidden fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-5 h-5 text-primary shrink-0" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Rechercher un Pal, un boss, un POI, un passif…"
            className="flex-1 bg-transparent text-sm sm:text-base focus:outline-none min-w-0"
          />
          <button data-testid="global-search-close" onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto scrollbar-thin" data-testid="global-search-results">
          {q.trim() && results.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="global-search-empty">
              Aucun résultat pour « {q} »
            </div>
          )}
          {!q.trim() && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              Tape pour chercher parmi les <span className="text-primary font-semibold">Pals</span>, la <span className="text-primary font-semibold">carte</span> et les <span className="text-primary font-semibold">passifs</span>.
            </div>
          )}
          {results.map((r, i) => {
            const Icon = GROUP_ICON[r.type];
            const activeItem = i === sel;
            return (
              <div
                key={r.key}
                onMouseEnter={() => setSel(i)}
                className={`w-full flex items-center gap-1 pr-2 transition-colors ${activeItem ? "bg-[hsl(var(--surface-hover))]" : "hover:bg-[hsl(var(--surface-hover))]"}`}
              >
                <button
                  data-testid={`global-search-result-${r.key}`}
                  onClick={() => choose(r)}
                  className="flex-1 min-w-0 flex items-center gap-3 px-4 py-2.5 text-left"
                >
                  {r.type === "pal" ? (
                    <PalAvatar pal={r.pal} size={32} />
                  ) : (
                    <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: `${r.color || "#00CCFF"}22`, border: `1px solid ${r.color || "#00CCFF"}`, color: r.color || "#00CCFF" }}>
                      <Icon className="w-4 h-4" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{r.label}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{r.sub}</div>
                  </div>
                  {activeItem && <CornerDownLeft className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                </button>
                {r.type === "pal" && (
                  <button
                    data-testid={`global-search-compare-${r.key}`}
                    onClick={(e) => { e.stopPropagation(); onClose(); openCompare(r.idx, null); }}
                    title="Comparer ce Pal"
                    className="shrink-0 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-[11px] font-semibold text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                  >
                    <Scale className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Comparer</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
